import { gsap } from 'gsap'

const DEFAULT_LAG = 0.4

// Alleen op echte muizen vanaf lg, waar de cursor-vervanging zin heeft
const POINTER_QUERY =
    '(hover: hover) and (pointer: fine) and (min-width: 1024px)'

const setup = (area, cursor) => {
    const prefersReduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
    ).matches

    const lag = prefersReduced
        ? 0
        : Number(cursor.dataset.hoverCursorLag) || DEFAULT_LAG

    const quickTo = {
        x: gsap.quickTo(cursor, 'x', { duration: lag, ease: 'power3' }),
        y: gsap.quickTo(cursor, 'y', { duration: lag, ease: 'power3' })
    }

    let rect = null

    const readRect = () => {
        rect = area.getBoundingClientRect()
    }

    const onMove = (event) => {
        if (!rect) return

        quickTo.x(event.clientX - rect.left)
        quickTo.y(event.clientY - rect.top)
    }

    const onEnter = (event) => {
        readRect()

        // Start én eind op de muispositie zetten. Zonder die tweede parameter
        // houdt quickTo zijn eigen startwaarde van de vorige keer vast en zie je
        // de cirkel bij het binnenkomen van zijn oude plek naar hier vliegen.
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top

        quickTo.x(x, x)
        quickTo.y(y, y)

        gsap.to(cursor, {
            opacity: 1,
            scale: 1,
            duration: 0.3,
            ease: 'power2.out'
        })

        // De pagina scrollt door terwijl je hovert, dus de rect blijft niet kloppen
        window.addEventListener('scroll', readRect, { passive: true })
        window.addEventListener('resize', readRect)
    }

    const onLeave = () => {
        gsap.to(cursor, {
            opacity: 0,
            scale: 0.6,
            duration: 0.3,
            ease: 'power2.out'
        })

        window.removeEventListener('scroll', readRect)
        window.removeEventListener('resize', readRect)
    }

    gsap.set(cursor, { scale: 0.6, xPercent: -50, yPercent: -50 })

    area.addEventListener('mouseenter', onEnter)
    area.addEventListener('mousemove', onMove)
    area.addEventListener('mouseleave', onLeave)

    return () => {
        area.removeEventListener('mouseenter', onEnter)
        area.removeEventListener('mousemove', onMove)
        area.removeEventListener('mouseleave', onLeave)
        onLeave()
    }
}

/**
 * Laat het `[data-hover-cursor]` element binnen elke `[data-hover-cursor-area]`
 * de muis volgen zolang je over die zone hangt. De zone moet `relative` zijn;
 * `data-hover-cursor-lag` op de cursor stelt de naloop in seconden in.
 *
 * @param {Document|Element|string} [root] Waar naar zones gezocht wordt.
 */
export const initHoverCursor = (root = document) => {
    const rootEl =
        typeof root === 'string' ? document.querySelector(root) : root

    if (!rootEl) return

    const pointerQuery = window.matchMedia(POINTER_QUERY)

    rootEl.querySelectorAll('[data-hover-cursor-area]').forEach((area) => {
        if (area.dataset.hoverCursorInitialized === 'true') return

        const cursor = area.querySelector('[data-hover-cursor]')

        if (!cursor) return

        area.dataset.hoverCursorInitialized = 'true'

        let teardown = null

        const sync = () => {
            if (pointerQuery.matches && !teardown) {
                teardown = setup(area, cursor)
            } else if (!pointerQuery.matches && teardown) {
                teardown()
                teardown = null
            }
        }

        sync()
        pointerQuery.addEventListener('change', sync)
    })
}

const MAGNETIC_MAX = 50
const MAGNETIC_STRENGTH = 0.35
const MAGNETIC_LAG = 0.5

const setupMagnetic = (area, target, badge) => {
    const prefersReduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
    ).matches

    const max = Number(badge.dataset.magneticCursorMax) || MAGNETIC_MAX
    const strength =
        Number(badge.dataset.magneticCursorStrength) || MAGNETIC_STRENGTH
    const duration = prefersReduced ? 0 : MAGNETIC_LAG

    const quickTo = {
        x: gsap.quickTo(badge, 'x', { duration, ease: 'power3' }),
        y: gsap.quickTo(badge, 'y', { duration, ease: 'power3' })
    }

    let center = null

    const readCenter = () => {
        const rect = area.getBoundingClientRect()

        center = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
        }
    }

    const clamp = (value) => Math.max(-max, Math.min(max, value))

    const onMove = (event) => {
        if (!center) return

        // Niet de muis zelf volgen: een fractie van de afstand tot het midden,
        // afgetopt op max. Zo blijft de cirkel op de image hangen, ook als je
        // over de tekst onder de kaart beweegt.
        quickTo.x(clamp((event.clientX - center.x) * strength))
        quickTo.y(clamp((event.clientY - center.y) * strength))
    }

    // Zichtbaarheid (opacity) is puur CSS via :hover op de kaart, zie
    // hover-cursor.twig — dat kan nooit "blijven hangen" zoals een JS
    // mouseenter/mouseleave-koppel dat soms mist bij razendsnel bewegen. JS
    // regelt hier alleen nog positie en de scale-pop.
    const onEnter = (event) => {
        readCenter()
        onMove(event)

        gsap.to(badge, {
            scale: 1,
            duration: 0.4,
            ease: 'power2.out'
        })

        // De pagina scrollt door terwijl je hovert, dus het midden verschuift
        window.addEventListener('scroll', readCenter, { passive: true })
        window.addEventListener('resize', readCenter)
    }

    const onLeave = () => {
        quickTo.x(0)
        quickTo.y(0)

        gsap.to(badge, {
            scale: 0.85,
            duration: 0.3,
            ease: 'power2.out'
        })

        window.removeEventListener('scroll', readCenter)
        window.removeEventListener('resize', readCenter)
    }

    gsap.set(badge, { scale: 0.85, xPercent: -50, yPercent: -50 })

    target.addEventListener('mouseenter', onEnter)
    target.addEventListener('mousemove', onMove)
    target.addEventListener('mouseleave', onLeave)

    return () => {
        target.removeEventListener('mouseenter', onEnter)
        target.removeEventListener('mousemove', onMove)
        target.removeEventListener('mouseleave', onLeave)
        onLeave()
    }
}

/**
 * Laat het `[data-magnetic-cursor]` element binnen elke `[data-magnetic-area]`
 * gecentreerd op die zone staan en de muis een beetje volgen. De zone moet
 * `relative` zijn. Ligt de zone in een link, dan luistert het effect op die
 * link, zodat de hele kaart de cirkel laat verschijnen.
 *
 * @param {Document|Element|string} [root] Waar naar zones gezocht wordt.
 */
export const initMagneticCursor = (root = document) => {
    const rootEl =
        typeof root === 'string' ? document.querySelector(root) : root

    if (!rootEl) return

    const pointerQuery = window.matchMedia(POINTER_QUERY)

    rootEl.querySelectorAll('[data-magnetic-area]').forEach((area) => {
        if (area.dataset.magneticCursorInitialized === 'true') return

        const badge = area.querySelector('[data-magnetic-cursor]')

        if (!badge) return

        area.dataset.magneticCursorInitialized = 'true'

        const target = area.closest('a') ?? area

        let teardown = null

        const sync = () => {
            if (pointerQuery.matches && !teardown) {
                teardown = setupMagnetic(area, target, badge)
            } else if (!pointerQuery.matches && teardown) {
                teardown()
                teardown = null
            }
        }

        sync()
        pointerQuery.addEventListener('change', sync)
    })
}
